/**
 * Progress travels to the account compressed (gzip, then base64) — typically
 * 10–20× smaller than the raw JSON. Browsers without CompressionStream send
 * plain JSON instead; the server accepts both and never reads the contents.
 */

export type ProgressFormat = 'gzip-base64-v1' | 'json-v1';

async function pipe(bytes: Uint8Array, stream: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const out = new Blob([bytes as BlobPart]).stream().pipeThrough(stream as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  return new Uint8Array(await new Response(out).arrayBuffer());
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

const fromBase64 = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export async function encodeProgress(json: string): Promise<{ format: ProgressFormat; data: string }> {
  if (typeof CompressionStream === 'undefined') return { format: 'json-v1', data: json };
  return { format: 'gzip-base64-v1', data: toBase64(await pipe(new TextEncoder().encode(json), new CompressionStream('gzip'))) };
}

export async function decodeProgress(format: string, data: string): Promise<string> {
  if (format === 'json-v1') return data;
  if (format === 'gzip-base64-v1') return new TextDecoder().decode(await pipe(fromBase64(data), new DecompressionStream('gzip')));
  throw new Error(`Unknown progress format: ${format}`);
}
